import React, { useState } from 'react';
import { Alert, Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { componentRows } from './componentProfileData';
import { hasInventory, formatFetchedAt } from './fleetPanelData';

const devices = n => `${n} device${n === 1 ? '' : 's'}`;

// BugZero Orange (--bz-orange-1). Non-provisionable modules — base OS,
// install/upgrade, platform layers, bundled products — are marked in this
// colour because switching one off rarely removes work and can hide issues
// that do apply. Inline (not a token class) so no SCSS recompile is needed.
const BZ_ORANGE = '#E1523D';

// Short line under the Core Components heading — same length/register as the
// main card explainer.
const BUILTIN_NOTE = 'These ship enabled on every product — the guideline is to leave them on.';

// Fuller warning, shown in the confirm modal only.
const NON_PROV_DISCLAIMER =
  'These are not separately provisionable modules — they cover things like the ' +
  'base OS, install/upgrade, platform layers and bundled products. A bug or CVE ' +
  'rarely affects only one of them, so switching one off seldom removes work and ' +
  'can hide issues that still apply to you. Turn one off only if you are certain ' +
  'it does not apply.';

/**
 * Which modules the fleet runs, and the reviewer's overrides.
 *
 * Rendered as a card in the project view's existing list idiom, not as a new
 * kind of surface — this is an addition to the page, not a redesign of it.
 *
 * Modules no device runs are hidden behind a toggle. A large fleet's product
 * can carry dozens of modules of which a handful are in use, and a grid
 * showing all of them buries the ones that matter under the ones that do not.
 *
 * A manually created project has no CMDB snapshot to draw counts, staleness,
 * or conflicts from — its modules come from the union of `affected_components`
 * across its own issues (see `useManualComponents`). `manualComponents` being
 * an array (even empty) switches the card into that stripped-down mode; it
 * being absent leaves the fleet path byte-for-byte as it was before this mode
 * existed. In manual mode each entry carries a `provisionable` flag: entries
 * that are not real, separately-provisionable modules are grouped last, marked
 * in BugZero Orange, and gated behind a confirm dialog before they can be
 * switched off — a display distinction only (marking applies no exemptions).
 */
const ComponentProfilePanel = ({
  project,
  onToggle,
  saving,
  toggleError,
  onRefresh,
  refreshing,
  refreshError,
  manualComponents
}) => {
  const [showUnused, setShowUnused] = useState(false);
  const [pending, setPending] = useState(null); // {kind:'single', component} | {kind:'batchOff'}
  const [busy, setBusy] = useState(false);
  const manual = Array.isArray(manualComponents);
  if (!manual && !hasInventory(project)) return null;
  if (manual && manualComponents.length === 0) return null;

  const rows = manual ? componentRows(project, { manualComponents }) : componentRows(project);
  // Non-modular vendors (e.g. Windows Server) and fleets whose CMDB reports no
  // module configuration have no components to review. Keep the card, though:
  // its header holds the project's only Refresh control.
  const empty = rows.length === 0;

  const used = rows.filter(r => r.enabledCiCount > 0 || r.isOverridden);
  const unused = rows.filter(r => !used.includes(r));
  const visible = manual ? rows : showUnused ? rows : used;
  const when = formatFetchedAt((project.inventorySnapshot || {}).fetchedAt);

  // Manual-mode groupings: real modules first, non-provisionable ones last.
  const provisionable = manual ? rows.filter(r => r.provisionable) : [];
  const nonProvisionable = manual ? rows.filter(r => !r.provisionable) : [];

  const setAllIn = async (targetRows, enabled) => {
    setBusy(true);
    try {
      for (const row of targetRows) {
        if (row.enabled !== enabled) {
          // eslint-disable-next-line no-await-in-loop
          await onToggle(row.component, enabled);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const handleManualToggle = row => {
    // Switching a builtin (non-provisionable) module OFF is the consequential
    // action and needs confirmation; turning anything back on is safe.
    if (!row.provisionable && row.enabled) {
      setPending({ kind: 'single', component: row.component });
      return;
    }
    onToggle(row.component, !row.enabled);
  };

  // Disabling builtin components in bulk gets the same confirmation as one-off.
  const handleDisableBuiltin = () => {
    if (nonProvisionable.some(r => r.enabled)) {
      setPending({ kind: 'batchOff' });
      return;
    }
    setAllIn(nonProvisionable, false);
  };

  const confirmPending = async () => {
    const p = pending;
    setPending(null);
    if (!p) return;
    if (p.kind === 'single') await onToggle(p.component, false);
    else if (p.kind === 'batchOff') await setAllIn(nonProvisionable, false);
  };

  // One master toggle per section, in place of Enable all / Disable all. On when
  // every row in the section is on; flipping it enables or disables the whole
  // section. `onDisableAll` lets the core section route its "off" through the
  // confirm modal; `orange` matches the core toggles' colour.
  const sectionToggle = (sectionRows, { orange, onDisableAll }) => {
    const allOn = sectionRows.length > 0 && sectionRows.every(r => r.enabled);
    return (
      <div className="d-flex align-items-center gap-2">
        {busy && <Spinner size="sm" />}
        <span className="text-muted small">All</span>
        <button
          type="button"
          className="bst-switch"
          role="switch"
          aria-checked={allOn}
          aria-label={allOn ? 'Disable all' : 'Enable all'}
          title={allOn ? 'Disable all' : 'Enable all'}
          disabled={saving || busy}
          style={orange && allOn ? { backgroundColor: BZ_ORANGE, borderColor: BZ_ORANGE } : undefined}
          onClick={() => (allOn ? onDisableAll() : setAllIn(sectionRows, true))}
        />
      </div>
    );
  };

  const renderManualTile = row => (
    <div className="bst-component-tile" key={row.component}>
      <div>
        <div className="bst-component-tile-name">{row.component}</div>
      </div>
      <button
        type="button"
        className="bst-switch"
        role="switch"
        aria-checked={row.enabled}
        aria-label={row.component}
        disabled={saving || busy}
        style={!row.provisionable && row.enabled ? { backgroundColor: BZ_ORANGE, borderColor: BZ_ORANGE } : undefined}
        onClick={() => handleManualToggle(row)}
      />
    </div>
  );

  // Manual project: the normal components and the builtin components are two
  // separate cards (siblings), so both headings sit at the same indent.
  if (manual) {
    return (
      <>
        <div className="bst-version-list mb-3">
          <div className="bst-version-list-header">
            <h3>Components</h3>
            {provisionable.length > 0 &&
              sectionToggle(provisionable, { orange: false, onDisableAll: () => setAllIn(provisionable, false) })}
          </div>
          <div className="p-3">
            <p className="bst-component-explainer">
              Switch a module off and its bugs and CVEs are marked <em>Config removed</em>.
            </p>
            {toggleError && (
              <Alert color="danger" role="alert">
                {toggleError}
              </Alert>
            )}
            <div className="bst-component-grid">{provisionable.map(renderManualTile)}</div>
          </div>
        </div>

        {nonProvisionable.length > 0 && (
          <div className="bst-version-list mb-3">
            <div className="bst-version-list-header">
              <h3>Core Components</h3>
              {sectionToggle(nonProvisionable, { orange: true, onDisableAll: handleDisableBuiltin })}
            </div>
            <div className="p-3">
              <p className="bst-component-explainer">{BUILTIN_NOTE}</p>
              <div className="bst-component-grid">{nonProvisionable.map(renderManualTile)}</div>
            </div>
          </div>
        )}

        <Modal isOpen={Boolean(pending)} toggle={() => setPending(null)} centered>
          <ModalHeader toggle={() => setPending(null)}>
            {pending && pending.kind === 'batchOff' ? 'Disable all core components?' : 'Switch this component off?'}
          </ModalHeader>
          <ModalBody>
            <p>{NON_PROV_DISCLAIMER}</p>
            {pending && pending.kind === 'batchOff' && (
              <p className="mb-0">
                <strong>Disable all</strong> switches off every core component listed here.
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setPending(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={confirmPending}
              disabled={busy}
              style={{ backgroundColor: BZ_ORANGE, borderColor: BZ_ORANGE }}
            >
              {pending && pending.kind === 'batchOff' ? 'Disable all anyway' : 'Switch off anyway'}
            </Button>
          </ModalFooter>
        </Modal>
      </>
    );
  }

  return (
    <div className="bst-version-list mb-3">
      <div className="bst-version-list-header">
        <h3>{empty ? 'Your inventory' : 'Components in your inventory'}</h3>
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted small" title={when.title}>
            from your CMDB · updated {when.text}
          </span>
          {onRefresh && (
            <Button color="link" size="sm" className="p-0" onClick={onRefresh} disabled={refreshing}>
              {refreshing ? <Spinner size="sm" /> : 'Refresh'}
            </Button>
          )}
        </div>
      </div>

      <div className="p-3">
        {empty ? (
          <p className="text-muted mb-0">No module configuration to review for these devices.</p>
        ) : (
          <p className="bst-component-explainer">
            Bugs and CVEs affecting only a module <strong>no device runs</strong> are marked <em>Config removed</em> and
            drop out of your reviews. Switch a module on or off to override what your CMDB reports.
          </p>
        )}

        {refreshError && (
          <Alert color="danger" role="alert">
            {refreshError}
          </Alert>
        )}
        {toggleError && (
          <Alert color="danger" role="alert">
            {toggleError}
          </Alert>
        )}

        {!empty && (
          <div className="bst-component-grid">
            {visible.map(row => (
              <div className="bst-component-tile" key={row.component}>
                <div>
                  <div className="bst-component-tile-name">{row.component}</div>
                  <div className="bst-component-tile-count">
                    {row.inProfile ? (
                      <>
                        {row.enabledCiCount} of {devices(row.configuredCiCount)}
                      </>
                    ) : (
                      'not in the latest scan'
                    )}
                    {row.autoOff && ' · off automatically'}
                  </div>
                  {/* Shown, never acted on: an override is the reviewer's
                    decision and is not silently flipped by a later scan. */}
                  {row.conflict && (
                    <div className="bst-component-tile-conflict">
                      You turned this {row.enabled ? 'on' : 'off'}, but CI data reports it{' '}
                      {row.enabled ? 'disabled' : 'enabled'}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="bst-switch"
                  role="switch"
                  aria-checked={row.enabled}
                  aria-label={row.component}
                  disabled={saving}
                  onClick={() => onToggle(row.component, !row.enabled)}
                />
              </div>
            ))}
          </div>
        )}

        {unused.length > 0 && (
          <Button color="link" size="sm" className="p-0 mt-3" onClick={() => setShowUnused(!showUnused)}>
            {showUnused ? 'Hide' : 'Show'} {unused.length} module{unused.length === 1 ? '' : 's'} no device runs
          </Button>
        )}
      </div>
    </div>
  );
};

export default ComponentProfilePanel;
